import { c as _c } from "react/compiler-runtime";
import { EntityUtils } from '@record-it-npm/synchro-client';
import React, { ComponentProps, FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageBox } from 'record-it-components/misc/MessageBox/MessageBox';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { LatLong, LocationUtils } from 'record-it-utils/location/LocationUtils/LocationUtils';
import { ConstructionSite } from '../../../classes/entities/ConstructionSite/constructionSiteTypes';
import { ConstructionSiteToSelectListItem } from '../ConstructionSiteToSelectListItem/ConstructionSiteToSelectListItem';
export type NearbyConstructionSitesToSelectListProps = PropsWithoutChildren<ComponentProps<'div'>> & {
  constructionSites: Array<ConstructionSite>;
  currentLocation: LatLong;
  onConstructionSiteSelected: (constructionSite: ConstructionSite) => void;
};
export const NearbyConstructionSitesToSelectList: FunctionComponent<NearbyConstructionSitesToSelectListProps> = React.memo(props => {
  const $ = _c(22);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let constructionSites;
  let currentLocation;
  let onConstructionSiteSelected;
  let remainingProps;
  if ($[2] !== t0) {
    ({
      currentLocation,
      constructionSites,
      onConstructionSiteSelected,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = constructionSites;
    $[4] = currentLocation;
    $[5] = onConstructionSiteSelected;
    $[6] = remainingProps;
  } else {
    constructionSites = $[3];
    currentLocation = $[4];
    onConstructionSiteSelected = $[5];
    remainingProps = $[6];
  }
  const {
    t
  } = useTranslation();
  let t1;
  if ($[7] !== constructionSites || $[8] !== currentLocation) {
    const constructionSitesWithDistance = getConstructionSitesWithDistance(constructionSites, currentLocation);
    constructionSitesWithDistance.sort(_temp);
    t1 = constructionSitesWithDistance.slice(0, 5);
    $[7] = constructionSites;
    $[8] = currentLocation;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const displayedConstructionSitesWithDistance = t1;
  let t2;
  if ($[10] !== displayedConstructionSitesWithDistance || $[11] !== onConstructionSiteSelected) {
    let t3;
    if ($[13] !== onConstructionSiteSelected) {
      t3 = t4 => {
        const {
          constructionSite
        } = t4;
        return <ConstructionSiteToSelectListItem key={EntityUtils.getTrackingKey(constructionSite)} constructionSite={constructionSite} onConstructionSiteSelected={onConstructionSiteSelected} data-testid="NearbyConstructionSitesToSelectList.ListItem" />;
      };
      $[13] = onConstructionSiteSelected;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    t2 = displayedConstructionSitesWithDistance.map(t3);
    $[10] = displayedConstructionSitesWithDistance;
    $[11] = onConstructionSiteSelected;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  if ($[15] !== displayedConstructionSitesWithDistance.length || $[16] !== t) {
    t3 = displayedConstructionSitesWithDistance.length === 0 ? <MessageBox data-testid="NearbyConstructionSitesToSelectList.NoNearbyConstructionSites">{t("components.constructionSite.NearbyConstructionSitesToSelectList.noNearbyConstructionSites")}</MessageBox> : null;
    $[15] = displayedConstructionSitesWithDistance.length;
    $[16] = t;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== remainingProps || $[19] !== t2 || $[20] !== t3) {
    t4 = <div {...remainingProps}>{t2}{t3}</div>;
    $[18] = remainingProps;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  return t4;
});
function getConstructionSitesWithDistance(constructionSites: Array<ConstructionSite>, currentLocation: LatLong): Array<ConstructionSiteWithDistance> {
  const constructionSitesWithDistance: Array<ConstructionSiteWithDistance> = [];
  for (const constructionSite of constructionSites) {
    if (!constructionSite.location) {
      continue;
    }
    constructionSitesWithDistance.push({
      constructionSite,
      distance: LocationUtils.distanceBetweenCoordinates(constructionSite.location, currentLocation)
    });
  }
  return constructionSitesWithDistance;
}
type ConstructionSiteWithDistance = {
  constructionSite: ConstructionSite;
  distance: number;
};
function _temp(a, b) {
  return a.distance - b.distance;
}